import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Calendar,
  Plus,
  Edit3,
  Trash2,
  Users,
  MapPin,
  Clock,
  DollarSign,
  Video,
  Globe,
  Eye,
  EyeOff,
  Loader2,
  X,
  Check,
  AlertCircle,
  ChevronDown,
  Download
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  { value: 'in_person', label: 'In Person', icon: MapPin },
  { value: 'online', label: 'Online', icon: Video },
  { value: 'hybrid', label: 'Hybrid', icon: Globe }
];

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600' },
  published: { label: 'Published', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

// Event Form Modal
const EventFormModal = ({ event, onClose, onSuccess }) => {
  const isEditing = !!event;
  const [formData, setFormData] = useState({
    title: event?.title || '',
    description: event?.description || '',
    event_type: event?.event_type || 'in_person',
    date: event?.date || '',
    time: event?.time || '',
    end_time: event?.end_time || '',
    timezone: event?.timezone || 'Australia/Melbourne',
    location: event?.location || '',
    meeting_link: event?.meeting_link || '',
    chapter: event?.chapter || '',
    capacity: event?.capacity || '',
    price: event?.price || 0,
    member_price: event?.member_price || '',
    is_members_only: event?.is_members_only || false,
    tags: event?.tags?.join(', ') || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.date || !formData.time) {
      setError('Please fill in title, date, and time');
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      ...formData,
      capacity: formData.capacity ? parseInt(formData.capacity) : null,
      price: parseFloat(formData.price) || 0,
      member_price: formData.member_price ? parseFloat(formData.member_price) : null,
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : []
    };

    try {
      const url = isEditing 
        ? `${API_URL}/api/events/admin/${event.id}`
        : `${API_URL}/api/events/admin/create`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSuccess();
        onClose();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save event');
      }
    } catch (err) {
      setError('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-serif text-stone-800">
            {isEditing ? 'Edit Event' : 'Create Event'}
          </h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Event Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Melbourne Letter Writing Gathering"
              className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-lws-green focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the event..."
              rows={3}
              className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-lws-green focus:border-transparent resize-none"
            />
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Event Type</label>
            <div className="grid grid-cols-3 gap-2">
              {EVENT_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    onClick={() => handleChange('event_type', type.value)}
                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                      formData.event_type === type.value
                        ? 'border-lws-green bg-lws-green/10 text-lws-green'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {type.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Start Time *</label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => handleChange('time', e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">End Time</label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => handleChange('end_time', e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              />
            </div>
          </div>

          {/* Location */}
          {(formData.event_type === 'in_person' || formData.event_type === 'hybrid') && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Venue name and address"
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              />
            </div>
          )}

          {/* Meeting Link */}
          {(formData.event_type === 'online' || formData.event_type === 'hybrid') && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Meeting Link</label>
              <input
                type="url"
                value={formData.meeting_link}
                onChange={(e) => handleChange('meeting_link', e.target.value)}
                placeholder="Zoom or Google Meet link"
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              />
            </div>
          )}

          {/* Chapter */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Chapter (optional)</label>
            <input
              type="text"
              value={formData.chapter}
              onChange={(e) => handleChange('chapter', e.target.value)}
              placeholder="e.g., Melbourne, Sydney"
              className="w-full px-4 py-2 border border-stone-200 rounded-lg"
            />
          </div>

          {/* Capacity & Pricing */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Capacity</label>
              <input
                type="number"
                value={formData.capacity}
                onChange={(e) => handleChange('capacity', e.target.value)}
                placeholder="Unlimited"
                min="1"
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Price (AUD)</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                placeholder="0 = Free"
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Member Price</label>
              <input
                type="number"
                value={formData.member_price}
                onChange={(e) => handleChange('member_price', e.target.value)}
                placeholder="Same as regular"
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              />
            </div>
          </div>

          {/* Members Only Toggle */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleChange('is_members_only', !formData.is_members_only)}
              className={`w-12 h-6 rounded-full transition-colors ${
                formData.is_members_only ? 'bg-lws-green' : 'bg-stone-300'
              }`}
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                formData.is_members_only ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
            <span className="text-sm text-stone-600">Members only event</span>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Tags (comma separated)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
              placeholder="workshop, beginner, social"
              className="w-full px-4 py-2 border border-stone-200 rounded-lg"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-stone-200 flex gap-3 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={saving}
            className="flex-1 bg-lws-green hover:bg-lws-green/90"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            {isEditing ? 'Save Changes' : 'Create Event'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Registrations Modal
const RegistrationsModal = ({ event, onClose }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRegistrations();
  }, [event.id]);

  const fetchRegistrations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/admin/${event.id}/registrations`, {
        credentials: 'include'
      });
      const data = await response.json();
      setRegistrations(data.registrations || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Guests', 'Status', 'Registered At'];
    const rows = registrations.map(r => [
      r.name,
      r.email,
      r.guests,
      r.payment_status,
      new Date(r.created_at).toLocaleDateString()
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.title.replace(/\s+/g, '_')}_registrations.csv`;
    a.click();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-serif text-stone-800">Registrations</h2>
            <p className="text-sm text-stone-500">{event.title}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCSV}>
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
            <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
            </div>
          ) : registrations.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No registrations yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {registrations.map(reg => (
                <div key={reg.id} className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg">
                  <div className="w-10 h-10 bg-lws-green/10 rounded-full flex items-center justify-center text-lws-green font-medium">
                    {reg.name[0]}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-stone-800">{reg.name}</p>
                    <p className="text-sm text-stone-500">{reg.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{reg.guests + 1} {reg.guests === 0 ? 'person' : 'people'}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      reg.payment_status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      reg.payment_status === 'free' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {reg.payment_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Event Card Component
const EventCard = ({ event, onEdit, onDelete, onViewRegistrations, onPublish }) => {
  const [deleting, setDeleting] = useState(false);
  const statusConfig = STATUS_CONFIG[event.status] || STATUS_CONFIG.draft;
  const TypeIcon = EVENT_TYPES.find(t => t.value === event.event_type)?.icon || MapPin;

  const handleDelete = async () => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/events/admin/${event.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        onDelete();
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to delete');
      }
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  const togglePublish = async () => {
    const newStatus = event.status === 'published' ? 'draft' : 'published';
    try {
      const response = await fetch(`${API_URL}/api/events/admin/${event.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        onPublish();
      }
    } catch (err) {
      console.error('Toggle publish failed:', err);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-stone-200 p-5 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-medium text-stone-800 truncate">{event.title}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
            {event.is_members_only && (
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                Members Only
              </span>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(event.date)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {event.time}
            </span>
            <span className="flex items-center gap-1">
              <TypeIcon className="w-4 h-4" />
              {EVENT_TYPES.find(t => t.value === event.event_type)?.label}
            </span>
            {event.chapter && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {event.chapter}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 mt-3 text-sm">
            <span className="flex items-center gap-1 text-stone-600">
              <Users className="w-4 h-4" />
              {event.registration_count || 0} registered
              {event.capacity && ` / ${event.capacity}`}
            </span>
            {event.price > 0 ? (
              <span className="flex items-center gap-1 text-green-600">
                <DollarSign className="w-4 h-4" />
                ${event.price}
                {event.member_price !== null && event.member_price !== event.price && (
                  <span className="text-stone-400">(${event.member_price} members)</span>
                )}
              </span>
            ) : (
              <span className="text-blue-600">Free</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => onViewRegistrations(event)}
            className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg"
            title="View Registrations"
          >
            <Users className="w-4 h-4" />
          </button>
          <button
            onClick={togglePublish}
            className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg"
            title={event.status === 'published' ? 'Unpublish' : 'Publish'}
          >
            {event.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onEdit(event)}
            className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg"
            title="Edit"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
            title="Delete"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default function AdminEventsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingRegistrations, setViewingRegistrations] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchEvents = useCallback(async () => {
    try {
      const url = statusFilter === 'all' 
        ? `${API_URL}/api/events/admin/list`
        : `${API_URL}/api/events/admin/list?status=${statusFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();
      setEvents(data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchEvents();
    }
  }, [user, fetchEvents]);

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h1 className="text-xl font-serif text-stone-600">Admin Access Required</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-events-page">
      {/* Header */}
      <section className="bg-gradient-to-b from-lws-gold to-lws-gold/90 text-white py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-8 h-8" />
                <h1 className="text-3xl font-serif">Events</h1>
              </div>
              <p className="text-white/80">Create and manage gatherings, workshops, and chapter events</p>
            </div>
            <Button
              onClick={() => { setEditingEvent(null); setShowForm(true); }}
              className="bg-white text-lws-gold hover:bg-white/90"
              data-testid="create-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 px-6 border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto flex items-center gap-2">
          {['all', 'draft', 'published', 'cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status 
                  ? 'bg-lws-gold text-white' 
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </section>

      {/* Events List */}
      <section className="py-8 px-6">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
              <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-600">No events yet</h3>
              <p className="text-stone-400 mt-1 mb-4">Create your first event to get started.</p>
              <Button 
                onClick={() => { setEditingEvent(null); setShowForm(true); }}
                className="bg-lws-gold hover:bg-lws-gold/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {events.map(event => (
                <EventCard
                  key={event.id}
                  event={event}
                  onEdit={(e) => { setEditingEvent(e); setShowForm(true); }}
                  onDelete={fetchEvents}
                  onViewRegistrations={setViewingRegistrations}
                  onPublish={fetchEvents}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Event Form Modal */}
      <AnimatePresence>
        {showForm && (
          <EventFormModal
            event={editingEvent}
            onClose={() => { setShowForm(false); setEditingEvent(null); }}
            onSuccess={fetchEvents}
          />
        )}
      </AnimatePresence>

      {/* Registrations Modal */}
      <AnimatePresence>
        {viewingRegistrations && (
          <RegistrationsModal
            event={viewingRegistrations}
            onClose={() => setViewingRegistrations(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
